import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { abyssColors } from '@/theme/colors';
import { Box, BoxProps, Stack, Typography } from '@mui/material';
import { memo, useEffect, useRef, useState } from 'react';
import { CustomSkeleton } from './styles';

interface ImageProps extends Omit<BoxProps, 'component'> {
  src: string;
  alt: string;
  fallbackSize?: string;
  fallbackColor?: string;
  fallbackContent?: React.ReactNode;
  onImageError?: (error: Event) => void;
  onLoadStart?: () => void;
  onLoadEnd?: (success: boolean) => void;
  imgSx?: React.CSSProperties;
  hideErrorMessage?: boolean;
  fontSize?: string;
}

const ImageComponent = ({
  src,
  alt,
  sx,
  imgSx,
  fallbackSize = '3.2rem',
  fallbackColor = abyssColors.neutral[400],
  fallbackContent,
  onImageError,
  onLoadStart,
  onLoadEnd,
  hideErrorMessage = false,
  fontSize = '1.2rem',
  ...props
}: ImageProps) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const imgRef = useRef<HTMLImageElement>(null);
  const prevSrcRef = useRef<string>(src);

  useEffect(() => {
    // Only reset state if src actually changed
    if (prevSrcRef.current === src) {
      // Image is already loaded with this src, don't reset
      return;
    }

    prevSrcRef.current = src;
    setHasError(false);
    setIsLoading(true);
    onLoadStart?.();

    if (src.startsWith('data:image')) {
      try {
        atob(src.split(',')[1]);
      } catch (e) {
        setHasError(true);
        setIsLoading(false);
        onImageError?.(new Event('error'));
        onLoadEnd?.(false);
        return;
      }
    }

    // Check if image is already cached
    if (imgRef.current?.complete) {
      if (imgRef.current.naturalWidth === 0) {
        // Image failed to load from cache
        setHasError(true);
        setIsLoading(false);
        onImageError?.(new Event('error'));
        onLoadEnd?.(false);
      } else {
        setIsLoading(false);
        onLoadEnd?.(true);
      }
    }
  }, [src, onLoadStart, onLoadEnd, onImageError]);

  const handleError = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setHasError(true);
    setIsLoading(false);
    onImageError?.(event.nativeEvent);
    onLoadEnd?.(false);
  };

  const handleLoad = () => {
    setIsLoading(false);
    onLoadEnd?.(true);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...sx,
      }}
      {...props}
    >
      {hasError ? (
        fallbackContent || (
          <Stack alignItems="center" gap="0.8rem">
            <AbyssIcon
              iconName="fallback"
              style={{
                width: fallbackSize,
                height: fallbackSize,
              }}
            />
            {!hideErrorMessage && (
              <Typography fontSize={fontSize} color={fallbackColor} textAlign="center">
                Image failed to load
              </Typography>
            )}
          </Stack>
        )
      ) : (
        <>
          <img
            ref={imgRef}
            crossOrigin="use-credentials"
            src={src}
            alt={alt}
            onError={handleError}
            onLoad={handleLoad}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              opacity: isLoading ? 0 : 1,
              transition: 'opacity 0.2s',
              ...imgSx,
            }}
          />
          {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
              }}
            >
              <CustomSkeleton animation="wave" />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export const Image = memo(ImageComponent);
